import { FC, ReactElement, SyntheticEvent, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { SelectBlue } from '../../components/select-blue'
import { Social } from './partials/social'
import InputField from './partials/input-field'

export interface RegisterFormValues {
  full_name: string
  email: string
  role_id: number
  age: number
  password: string
}

interface RegisterFormProps {
  error: string
  isLanding: boolean
  isLoading: boolean
  handleSubmit: (values: RegisterFormValues) => void
  handleLoginLink: (e: SyntheticEvent) => void
}

export const RegisterForm: FC<RegisterFormProps> = ({
  error,
  isLanding = true,
  isLoading = false,
  handleSubmit,
  handleLoginLink,
}): ReactElement => {
  const [isPassword, setIsPassword] = useState(true)

  const RegisterSchema = Yup.object().shape({
    full_name: Yup.string().required('First and last is a required').min(6, 'Must be at least 6 characters'),
    email: Yup.string().required('Email is a required').email('Email must be a valid email'),
    role_id: Yup.number().required('Role is a required'),
    age: Yup.number().required(),
    password: Yup.string().required('Password is a required').min(6,'Must be at least 6 characters'),
  })

  const formik = useFormik<RegisterFormValues>({
    initialValues: {
      full_name: '',
      email: '',
      role_id: 0,
      age: 0,
      password: '',
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      handleSubmit(values)
    },
  })

  return (
    <>
      {error && (
        <div className="hbMessage">
          <div className="wrapperHb">
            <span>!</span>
            <p>{error}</p>
          </div>
        </div>
      )}

      <div className={`form ${isLanding ? 'iframe' : ''}`}>
        <div className="wrapper">
          <div className="form-logo">
            <a href={process.env.MIX_APP_API_HOST}>
              <img
                src="/account/img/header/logo-big.svg"
                width="231"
                height="32"
                alt=""
              />
            </a>
          </div>
          <p>
            New to Practithink?{' '}
            <a href="#" onClick={handleLoginLink}>
              Sign in here
            </a>
          </p>
          <h2 className="form-title">Create an account</h2>

          <Social />

          <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <InputField key="full_name" formik={formik} name="full_name">
              <label htmlFor="n-1">Name</label>
              <input
                className="input-fullName"
                id="n-1"
                type="text"
                name="full_name"
                placeholder="First and Last name"
                onChange={formik.handleChange}
                value={formik.values.full_name}
              />
            </InputField>

            <InputField key="email" formik={formik} name="email">
              <label htmlFor="n-2">Email</label>
              <input
                id="n-2"
                type="email"
                name="email"
                placeholder="Example: email@gmail.com"
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </InputField>

            <InputField key="role_id" formik={formik} name="role_id">
              <label htmlFor="n-3">
                Your role {formik.values.full_name.split(' ')[0]}
                <span className="label-fullName" />?
              </label>
              <SelectBlue
                formik={formik}
                name="role_id"
                options={[
                  { value: 1, label: 'Teacher' },
                  { value: 2, label: 'Student' },
                  { value: 3, label: 'Parent' },
                  { value: 4, label: 'Author' },
                ]}
              />
            </InputField>

            {Number(formik.values.role_id) === 2 && (
              <InputField key="age" formik={formik} name="age">
                <label htmlFor="n-3">Age</label>
                <SelectBlue
                  formik={formik}
                  name="age"
                  options={[
                    { value: 6, label: 'I’m 6' },
                    { value: 7, label: 'I’m 7' },
                    { value: 8, label: 'I’m 8' },
                    { value: 9, label: 'I’m 9' },
                    { value: 10, label: 'I’m 10' },
                    { value: 11, label: 'I’m 11' },
                    { value: 12, label: 'I’m 12' },
                  ]}
                />
              </InputField>
            )}

            <InputField key="password" formik={formik} name="password">
              <div className="input-block__top">
                <label htmlFor="n-5">Password</label>
                <span
                  className="btn-show"
                  style={{ userSelect: 'none' }}
                  onClick={() => {
                    setIsPassword(!isPassword)
                  }}
                >
                  {isPassword ? 'Show' : 'Hide'}
                </span>
              </div>
              <input
                id="n-5"
                type={isPassword ? 'password' : 'text'}
                name="password"
                placeholder="Password"
                onChange={formik.handleChange}
                value={formik.values.password}
              />
            </InputField>

            <p className="text-big">
              By creating an account, you accept the following Practithink{' '}
              <a target='_blank' href="https://practithink.com/terms-of-use" rel="noreferrer">Terms of service</a> and{' '}
              <a target='_blank' href="https://practithink.com/privacy-policy" rel="noreferrer">Privacy policy</a>
            </p>

            {isLoading ? (
              <button type="button" disabled className="primary-btn">
                Submitting
              </button>
            ) : (
              <button type="submit" className="primary-btn">
                Create an account
              </button>
            )}
          </form>
        </div>
      </div>
    </>
  )
}
